import React, { useEffect, useState, useRef } from "react";
import Swal from "sweetalert2";
import { CircularProgress } from "@mui/material";
import axiosInstance from "../../config/axiosConfig";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  (typeof window !== "undefined" && window.location.hostname !== "localhost" && window.location.hostname !== "127.0.0.1"
    ? "https://api.uo.edu.pk/api/"
    : "http://127.0.0.1:8001/api/");

const resolveImgUrl = (img) => {
  if (!img) return "";
  if (typeof img === "string" && (img.startsWith("http://") || img.startsWith("https://"))) {
    return img;
  }
  if (typeof img === "string" && img.startsWith("/images/")) {
    return `https://www.uo.edu.pk${img}`;
  }
  const base = (API_BASE_URL || "https://api.uo.edu.pk/api/").replace(/\/api\/?$/, "");
  return `${base}/api/media/${img}`;
};

const defaultPillars = [
  {
    title: "Campus Facilities",
    description:
      "Modern scientific and computer laboratories, a central library linked with the HEC digital database, hostels, transport, sports grounds, a central mosque, a Women Development Centre, and a technology incubator support study, research, and everyday campus life.",
  },
  {
    title: "Societies & Extracurriculars",
    description:
      "Students join sports teams, literary and art appreciation societies, and the university media club. Department-led events — such as the Entrepreneurship Gala by Management Sciences — build leadership, creativity, and professional networks beyond the classroom.",
  },
  {
    title: "Sports & Recreation",
    description:
      "The sports complex and dedicated grounds host inter-faculty competition and fitness for all levels. Sports Gala, organised under the Director Sports, highlights athletic excellence and student volunteering across the campus community.",
  },
  {
    title: "Skills, Innovation & Careers",
    description:
      "The Training & Skills Development Centre (TSDC) offers market-oriented short courses for students and professionals. ORIC promotes research commercialisation and startups, while career and overseas-placement seminars prepare students for local and global opportunities.",
  },
  {
    title: "Women Development Centre",
    description:
      "The Women Development Centre advances gender equity through seminars, entrepreneurship showcases, and leadership programmes. Celebrations bring students and faculty together to celebrate empowerment, skills, and inclusive campus culture.",
  },
  {
    title: "Community & Recognition",
    description:
      "Community outreach, cultural programmes, and international linkages connect students with Okara and the wider world. Rankings recognition reflects a growing culture of research and learning.",
  },
];

const defaultHighlights = [
  {
    title: "Sports Gala 2026",
    date: "February 2026",
    description:
      "A flagship three-day campus event under the Directorate of Sports, bringing faculties together for athletic competition, teamwork, and celebration — with recognition from the Vice Chancellor and university leadership.",
  },
  {
    title: "International Women's Day 2026",
    date: "March 2026",
    description:
      "The Women Development Centre hosted a seminar, poster competition, and entrepreneurial stalls by female students, spotlighting leadership, skill development, and equal opportunity on campus.",
  },
  {
    title: "3rd Convocation 2026",
    date: "January 2026",
    description:
      "UO celebrated its third convocation, honouring graduates across programmes and marking a major milestone in the university's academic calendar.",
  },
  {
    title: "Overseas Placement & Innovation",
    date: "March 2026",
    description:
      "ORIC and Student Affairs organised a seminar on overseas careers and entrepreneurship, connecting students with global placement pathways, startups, and industry partners.",
  },
];

const UniversityLifeAdmin = () => {
  const [activeTab, setActiveTab] = useState("general"); // general | gallery | pillars | highlights
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pageId, setPageId] = useState("page00000000000000000001");

  // Form states
  const [title, setTitle] = useState("University Life");
  const [subtitle, setSubtitle] = useState("Campus facilities, societies, sports, skills training, and vibrant student life");
  const [content, setContent] = useState("");
  const [bannerImg, setBannerImg] = useState("/images/okara-university-campus.webp");
  const [metaTitle, setMetaTitle] = useState("University Life | University of Okara");
  const [metaDescription, setMetaDescription] = useState("Experience vibrant student life, state-of-the-art facilities, societies, sports galas, and academic culture at the University of Okara.");
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);

  // Structured Sections & Highlights
  const [pillars, setPillars] = useState(defaultPillars);
  const [highlights, setHighlights] = useState(defaultHighlights);

  // Gallery
  const [gallery, setGallery] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchPageData();
  }, []);

  const fetchPageData = async () => {
    setLoading(true);
    try {
      // 1. Fetch CMS page data for life-at-uo
      const res = await axiosInstance.get("cms-pages/single/life-at-uo");
      const data = res?.data?.data;
      if (data) {
        if (data._id || data.id) setPageId(data._id || data.id);
        if (data.title) setTitle(data.title);
        if (data.subtitle) setSubtitle(data.subtitle);
        if (data.content) setContent(data.content);
        if (data.bannerImg) setBannerImg(data.bannerImg);
        if (data.metaTitle) setMetaTitle(data.metaTitle);
        if (data.metaDescription) setMetaDescription(data.metaDescription);

        // Sections or Highlights
        if (Array.isArray(data.sections) && data.sections.length > 0) {
          // Check if sections are highlights or pillars
          const hasDates = data.sections.some((s) => s.date);
          if (hasDates) {
            setHighlights(data.sections);
          } else {
            setPillars(data.sections);
          }
        }

        // Gallery
        if (Array.isArray(data.gallery) && data.gallery.length > 0) {
          setGallery(data.gallery);
        } else if (Array.isArray(data.attachments) && data.attachments.length > 0) {
          setGallery(data.attachments);
        }
      }
    } catch (err) {
      console.warn("Could not load cms-pages/single/life-at-uo, attempting fallback:", err);
    } finally {
      setLoading(false);
    }
  };

  // Handle banner image change
  const handleBannerFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setBannerFile(file);
      setBannerPreview(URL.createObjectURL(file));
    }
  };

  // Upload multiple images to gallery
  const handleUploadPhotos = async (files) => {
    if (!files || files.length === 0) return;
    setUploadingPhotos(true);

    try {
      const formData = new FormData();
      Array.from(files).forEach((file) => {
        formData.append("images[]", file);
      });

      // Try dedicated gallery endpoint first
      let uploadSuccess = false;
      try {
        const res = await axiosInstance.post(
          `cms-pages/${pageId}/gallery`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        if (res?.data?.data && Array.isArray(res.data.data)) {
          setGallery(res.data.data);
          uploadSuccess = true;
        }
      } catch (galleryEndpointErr) {
        console.log("Dedicated gallery upload endpoint not found or failed, uploading via fallback:", galleryEndpointErr);
      }

      // If dedicated endpoint failed, upload as data URLs / attachments
      if (!uploadSuccess) {
        const newItems = await Promise.all(
          Array.from(files).map((file) => {
            return new Promise((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => {
                resolve({
                  id: "img_" + Date.now() + "_" + Math.random().toString(36).substring(2, 7),
                  fileName: file.name,
                  originalName: file.name,
                  url: reader.result,
                  size: file.size,
                  caption: "",
                  createdAt: new Date().toISOString(),
                });
              };
              reader.readAsDataURL(file);
            });
          })
        );

        const updatedGallery = [...gallery, ...newItems];
        setGallery(updatedGallery);
      }

      Swal.fire({
        icon: "success",
        title: "Photos Uploaded!",
        text: `${files.length} photo(s) added to University Life gallery.`,
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Failed to upload gallery photos:", error);
      Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text: error?.response?.data?.message || "Could not upload images. Please try again.",
      });
    } finally {
      setUploadingPhotos(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Delete gallery photo
  const handleDeletePhoto = async (photo) => {
    const photoId = photo.id || photo.fileName;
    const result = await Swal.fire({
      title: "Delete photo?",
      text: "This image will be removed from the University Life gallery.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it",
    });

    if (!result.isConfirmed) return;

    try {
      try {
        await axiosInstance.delete(`cms-pages/${pageId}/gallery/${photoId}`);
      } catch (err) {
        console.log("Server delete route fallback:", err);
      }

      const updated = gallery.filter((p) => (p.id || p.fileName) !== photoId);
      setGallery(updated);
      Swal.fire({
        icon: "success",
        title: "Photo removed",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error deleting photo:", error);
      Swal.fire("Error", "Could not delete photo. Please try again.", "error");
    }
  };

  // Save full page updates
  const handleSavePage = async () => {
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("_method", "PUT");
      formData.append("title", title);
      formData.append("slug", "life-at-uo");
      formData.append("subtitle", subtitle);
      formData.append("content", content);
      formData.append("metaTitle", metaTitle);
      formData.append("metaDescription", metaDescription);
      formData.append("sections", JSON.stringify(highlights));
      formData.append("gallery", JSON.stringify(gallery));
      formData.append("attachments", JSON.stringify(gallery));

      if (bannerFile) {
        formData.append("bannerImg", bannerFile);
      }

      const targetId = pageId || "page00000000000000000001";
      await axiosInstance.post(`cms-pages/change/${targetId}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      Swal.fire({
        icon: "success",
        title: "Saved Successfully!",
        text: "University Life content and gallery have been updated on the website.",
        showConfirmButton: false,
        timer: 2000,
      });
      setBannerFile(null);
      fetchPageData();
    } catch (error) {
      console.error("Error saving University Life page:", error);
      Swal.fire({
        icon: "error",
        title: "Save Failed",
        text: error?.response?.data?.message || "Failed to update page. Please try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  // Pillar CRUD
  const handlePillarChange = (index, field, value) => {
    const updated = [...pillars];
    updated[index] = { ...updated[index], [field]: value };
    setPillars(updated);
  };

  const handleAddPillar = () => {
    setPillars([...pillars, { title: "New Feature / Pillar", description: "Describe this aspect of campus life..." }]);
  };

  const handleDeletePillar = (index) => {
    setPillars(pillars.filter((_, idx) => idx !== index));
  };

  // Highlights CRUD
  const handleHighlightChange = (index, field, value) => {
    const updated = [...highlights];
    updated[index] = { ...updated[index], [field]: value };
    setHighlights(updated);
  };

  const handleAddHighlight = () => {
    setHighlights([
      ...highlights,
      {
        title: "New Campus Highlight",
        date: "Current Month " + new Date().getFullYear(),
        description: "Highlight description...",
      },
    ]);
  };

  const handleDeleteHighlight = (index) => {
    setHighlights(highlights.filter((_, idx) => idx !== index));
  };

  return (
    <div style={{ padding: "24px", maxWidth: "1280px", margin: "0 auto" }}>
      {/* Top Header Card */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "16px",
          backgroundColor: "#fff",
          padding: "24px",
          borderRadius: "12px",
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
          marginBottom: "24px",
        }}
      >
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span style={{ fontSize: "28px" }}>🎓</span>
            <h1 style={{ margin: 0, fontSize: "24px", fontWeight: 800, color: "#1e293b" }}>
              University Life Management
            </h1>
          </div>
          <p style={{ margin: "6px 0 0", fontSize: "14px", color: "#64748b" }}>
            Update all texts, descriptions, recent event highlights, and upload multiple photo gallery images for the University Life page.
          </p>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <a
            href="https://www.uo.edu.pk/about/university-life"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              padding: "10px 18px",
              backgroundColor: "#f1f5f9",
              color: "#334155",
              borderRadius: "8px",
              textDecoration: "none",
              fontWeight: 600,
              fontSize: "13px",
              border: "1px solid #cbd5e1",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <span>View Live Page</span>
            <span>↗</span>
          </a>

          <button
            type="button"
            onClick={handleSavePage}
            disabled={saving}
            style={{
              padding: "10px 24px",
              backgroundColor: "#2563eb",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              fontWeight: 700,
              fontSize: "14px",
              cursor: saving ? "not-allowed" : "pointer",
              boxShadow: "0 2px 8px rgba(37, 99, 235, 0.3)",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            {saving ? (
              <>
                <CircularProgress size={16} style={{ color: "#fff" }} />
                <span>Saving Changes...</span>
              </>
            ) : (
              <span>💾 Save All Changes</span>
            )}
          </button>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div
        style={{
          display: "flex",
          gap: "8px",
          borderBottom: "2px solid #e2e8f0",
          marginBottom: "24px",
          backgroundColor: "#fff",
          padding: "8px 16px 0",
          borderRadius: "10px 10px 0 0",
        }}
      >
        {[
          { id: "general", label: "📄 Main Content & Cover", icon: "📝" },
          { id: "gallery", label: `📸 Photo Gallery (${gallery.length})`, icon: "🖼️" },
          { id: "highlights", label: `⭐ Campus Highlights (${highlights.length})`, icon: "🎉" },
          { id: "pillars", label: `🏛 Life Pillars (${pillars.length})`, icon: "🏢" },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: "12px 20px",
              border: "none",
              background: "none",
              borderBottom: activeTab === tab.id ? "3px solid #2563eb" : "3px solid transparent",
              color: activeTab === tab.id ? "#2563eb" : "#64748b",
              fontWeight: activeTab === tab.id ? 700 : 500,
              fontSize: "14px",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Loading state */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 0", backgroundColor: "#fff", borderRadius: "12px" }}>
          <CircularProgress size={36} />
          <p style={{ marginTop: "12px", color: "#64748b", fontWeight: 600 }}>Loading University Life content...</p>
        </div>
      ) : (
        <div>
          {/* TAB 1: General Content & Cover Image */}
          {activeTab === "general" && (
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: "12px",
                padding: "24px",
                boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
              }}
            >
              <h3 style={{ margin: "0 0 18px", fontSize: "18px", color: "#1e293b" }}>
                Page Information & Header Banner
              </h3>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "20px" }}>
                <div>
                  <label style={{ display: "block", fontWeight: 600, fontSize: "13px", color: "#334155", marginBottom: "6px" }}>
                    Page Title
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="University Life"
                    style={{
                      width: "100%",
                      padding: "10px 14px",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                      fontSize: "14px",
                      boxSizing: "border-box",
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: "block", fontWeight: 600, fontSize: "13px", color: "#334155", marginBottom: "6px" }}>
                    Page Tagline / Subtitle
                  </label>
                  <input
                    type="text"
                    value={subtitle}
                    onChange={(e) => setSubtitle(e.target.value)}
                    placeholder="Campus facilities, societies, sports, skills training, and vibrant student life"
                    style={{
                      width: "100%",
                      padding: "10px 14px",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                      fontSize: "14px",
                      boxSizing: "border-box",
                    }}
                  />
                </div>
              </div>

              {/* Cover Banner Image */}
              <div style={{ marginBottom: "24px" }}>
                <label style={{ display: "block", fontWeight: 600, fontSize: "13px", color: "#334155", marginBottom: "6px" }}>
                  Main Cover Banner Image
                </label>
                <div style={{ display: "flex", gap: "20px", alignItems: "flex-start", flexWrap: "wrap" }}>
                  <div
                    style={{
                      width: "320px",
                      height: "170px",
                      borderRadius: "10px",
                      overflow: "hidden",
                      border: "2px solid #e2e8f0",
                      backgroundColor: "#f8fafc",
                    }}
                  >
                    <img
                      src={bannerPreview || resolveImgUrl(bannerImg) || "/images/okara-university-campus.webp"}
                      alt="University Life Cover"
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      onError={(e) => {
                        e.currentTarget.src = "https://placehold.co/600x340?text=University+Life+Banner";
                      }}
                    />
                  </div>

                  <div style={{ flex: 1, minWidth: "260px" }}>
                    <p style={{ margin: "0 0 10px", fontSize: "13px", color: "#64748b" }}>
                      Upload a high-resolution campus photo (JPG, PNG, WEBP). Recommended size: 1600x600px.
                    </p>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleBannerFileChange}
                      style={{
                        display: "block",
                        padding: "8px",
                        fontSize: "13px",
                        borderRadius: "6px",
                        border: "1px solid #cbd5e1",
                        width: "100%",
                        boxSizing: "border-box",
                      }}
                    />
                    {bannerFile && (
                      <span style={{ fontSize: "12px", color: "#16a34a", fontWeight: 600, display: "inline-block", marginTop: "6px" }}>
                        ✓ New image selected: {bannerFile.name} (will upload on save)
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Main Introduction Text */}
              <div style={{ marginBottom: "24px" }}>
                <label style={{ display: "block", fontWeight: 600, fontSize: "13px", color: "#334155", marginBottom: "6px" }}>
                  Main Introduction / Overview Text
                </label>
                <textarea
                  rows={6}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Life at the University of Okara extends far beyond the classroom..."
                  style={{
                    width: "100%",
                    padding: "12px 14px",
                    borderRadius: "6px",
                    border: "1px solid #cbd5e1",
                    fontSize: "14px",
                    lineHeight: "1.6",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              {/* SEO Meta */}
              <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: "20px" }}>
                <h4 style={{ margin: "0 0 14px", fontSize: "15px", color: "#475569" }}>
                  Search Engine Optimization (SEO Metadata)
                </h4>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "12px", color: "#64748b", marginBottom: "4px" }}>
                      Meta Title
                    </label>
                    <input
                      type="text"
                      value={metaTitle}
                      onChange={(e) => setMetaTitle(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        borderRadius: "6px",
                        border: "1px solid #cbd5e1",
                        fontSize: "13px",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "12px", color: "#64748b", marginBottom: "4px" }}>
                      Meta Description
                    </label>
                    <input
                      type="text"
                      value={metaDescription}
                      onChange={(e) => setMetaDescription(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        borderRadius: "6px",
                        border: "1px solid #cbd5e1",
                        fontSize: "13px",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: Multiple Photo Gallery */}
          {activeTab === "gallery" && (
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: "12px",
                padding: "24px",
                boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                  flexWrap: "wrap",
                  gap: "12px",
                }}
              >
                <div>
                  <h3 style={{ margin: 0, fontSize: "18px", color: "#1e293b" }}>
                    University Life Photo Gallery
                  </h3>
                  <p style={{ margin: "4px 0 0", fontSize: "13px", color: "#64748b" }}>
                    Add multiple photos of campus activities, events, societies, labs, and student life. These display in an interactive grid on the website.
                  </p>
                </div>

                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) => handleUploadPhotos(e.target.files)}
                    style={{ display: "none" }}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingPhotos}
                    style={{
                      padding: "10px 20px",
                      backgroundColor: "#059669",
                      color: "#fff",
                      border: "none",
                      borderRadius: "8px",
                      fontWeight: 700,
                      fontSize: "13px",
                      cursor: uploadingPhotos ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      boxShadow: "0 2px 6px rgba(5, 150, 105, 0.25)",
                    }}
                  >
                    {uploadingPhotos ? (
                      <>
                        <CircularProgress size={16} style={{ color: "#fff" }} />
                        <span>Uploading Photos...</span>
                      </>
                    ) : (
                      <span>+ Add Multiple Photos</span>
                    )}
                  </button>
                </div>
              </div>

              {/* Drag and Drop Zone */}
              <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  if (e.dataTransfer.files) handleUploadPhotos(e.dataTransfer.files);
                }}
                style={{
                  border: "2px dashed #94a3b8",
                  borderRadius: "12px",
                  padding: "30px 20px",
                  textAlign: "center",
                  backgroundColor: "#f8fafc",
                  cursor: "pointer",
                  marginBottom: "24px",
                  transition: "border 0.2s ease",
                }}
              >
                <span style={{ fontSize: "36px", display: "block", marginBottom: "8px" }}>📷</span>
                <p style={{ margin: 0, fontWeight: 700, fontSize: "15px", color: "#334155" }}>
                  Click to select photos or drag & drop images here
                </p>
                <p style={{ margin: "4px 0 0", fontSize: "12px", color: "#64748b" }}>
                  Upload multiple photos at once (JPG, PNG, WEBP)
                </p>
              </div>

              {/* Gallery Grid */}
              {gallery.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px", backgroundColor: "#f8fafc", borderRadius: "10px" }}>
                  <p style={{ color: "#94a3b8", fontSize: "14px", fontStyle: "italic", margin: 0 }}>
                    No gallery photos added yet. Upload photos above to display them on the University Life page.
                  </p>
                </div>
              ) : (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
                    gap: "18px",
                  }}
                >
                  {gallery.map((photo, idx) => {
                    const photoUrl = resolveImgUrl(photo.url || photo.fileName);
                    return (
                      <div
                        key={photo.id || idx}
                        style={{
                          borderRadius: "10px",
                          overflow: "hidden",
                          border: "1px solid #e2e8f0",
                          backgroundColor: "#fff",
                          boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <div
                          style={{
                            height: "160px",
                            overflow: "hidden",
                            position: "relative",
                            cursor: "pointer",
                            backgroundColor: "#f1f5f9",
                          }}
                          onClick={() => setLightboxPhoto(photoUrl)}
                        >
                          <img
                            src={photoUrl}
                            alt={photo.caption || photo.originalName || "University Life Photo"}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            onError={(e) => {
                              e.currentTarget.src = "https://placehold.co/400x300?text=Photo";
                            }}
                          />
                          <span
                            style={{
                              position: "absolute",
                              top: "8px",
                              left: "8px",
                              backgroundColor: "rgba(0,0,0,0.6)",
                              color: "#fff",
                              fontSize: "11px",
                              fontWeight: 700,
                              padding: "2px 7px",
                              borderRadius: "6px",
                            }}
                          >
                            #{idx + 1}
                          </span>
                        </div>

                        <div style={{ padding: "12px", flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                          <div>
                            <input
                              type="text"
                              value={photo.caption || ""}
                              onChange={(e) => {
                                const updated = [...gallery];
                                updated[idx] = { ...updated[idx], caption: e.target.value };
                                setGallery(updated);
                              }}
                              placeholder="Add optional caption..."
                              style={{
                                width: "100%",
                                padding: "6px 8px",
                                fontSize: "12px",
                                borderRadius: "4px",
                                border: "1px solid #cbd5e1",
                                boxSizing: "border-box",
                                marginBottom: "8px",
                              }}
                            />
                            {photo.originalName && (
                              <p style={{ margin: 0, fontSize: "11px", color: "#94a3b8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {photo.originalName}
                              </p>
                            )}
                          </div>

                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "10px", paddingTop: "8px", borderTop: "1px solid #f1f5f9" }}>
                            <button
                              type="button"
                              onClick={() => setLightboxPhoto(photoUrl)}
                              style={{
                                border: "none",
                                background: "none",
                                color: "#2563eb",
                                fontSize: "12px",
                                fontWeight: 600,
                                cursor: "pointer",
                                padding: 0,
                              }}
                            >
                              🔍 Preview
                            </button>

                            <button
                              type="button"
                              onClick={() => handleDeletePhoto(photo)}
                              style={{
                                border: "none",
                                background: "#fee2e2",
                                color: "#dc2626",
                                fontSize: "11px",
                                fontWeight: 600,
                                padding: "4px 8px",
                                borderRadius: "4px",
                                cursor: "pointer",
                              }}
                            >
                              🗑 Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* TAB 3: Campus Highlights */}
          {activeTab === "highlights" && (
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: "12px",
                padding: "24px",
                boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: "18px", color: "#1e293b" }}>
                    Recent Campus Highlights & Events
                  </h3>
                  <p style={{ margin: "4px 0 0", fontSize: "13px", color: "#64748b" }}>
                    Highlight cards showcasing major annual events, convocations, seminars, and student milestones.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={handleAddHighlight}
                  style={{
                    padding: "8px 16px",
                    backgroundColor: "#16a34a",
                    color: "#fff",
                    border: "none",
                    borderRadius: "6px",
                    fontWeight: 600,
                    fontSize: "13px",
                    cursor: "pointer",
                  }}
                >
                  + Add New Highlight
                </button>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                {highlights.map((item, idx) => (
                  <div
                    key={idx}
                    style={{
                      border: "1px solid #cbd5e1",
                      borderRadius: "10px",
                      padding: "16px",
                      backgroundColor: "#f8fafc",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                      <span style={{ fontWeight: 700, fontSize: "13px", color: "#475569" }}>
                        Highlight #{idx + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleDeleteHighlight(idx)}
                        style={{
                          border: "none",
                          background: "#fee2e2",
                          color: "#dc2626",
                          fontSize: "11px",
                          fontWeight: 600,
                          padding: "3px 8px",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                      >
                        Remove
                      </button>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "12px", marginBottom: "10px" }}>
                      <div>
                        <label style={{ display: "block", fontSize: "12px", color: "#475569", marginBottom: "4px", fontWeight: 600 }}>
                          Event / Highlight Title
                        </label>
                        <input
                          type="text"
                          value={item.title || ""}
                          onChange={(e) => handleHighlightChange(idx, "title", e.target.value)}
                          style={{
                            width: "100%",
                            padding: "8px 10px",
                            fontSize: "13px",
                            borderRadius: "6px",
                            border: "1px solid #cbd5e1",
                            boxSizing: "border-box",
                          }}
                        />
                      </div>

                      <div>
                        <label style={{ display: "block", fontSize: "12px", color: "#475569", marginBottom: "4px", fontWeight: 600 }}>
                          Date / Badge
                        </label>
                        <input
                          type="text"
                          value={item.date || ""}
                          onChange={(e) => handleHighlightChange(idx, "date", e.target.value)}
                          placeholder="e.g. February 2026"
                          style={{
                            width: "100%",
                            padding: "8px 10px",
                            fontSize: "13px",
                            borderRadius: "6px",
                            border: "1px solid #cbd5e1",
                            boxSizing: "border-box",
                          }}
                        />
                      </div>
                    </div>

                    <div>
                      <label style={{ display: "block", fontSize: "12px", color: "#475569", marginBottom: "4px", fontWeight: 600 }}>
                        Description
                      </label>
                      <textarea
                        rows={3}
                        value={item.description || ""}
                        onChange={(e) => handleHighlightChange(idx, "description", e.target.value)}
                        style={{
                          width: "100%",
                          padding: "8px 10px",
                          fontSize: "13px",
                          borderRadius: "6px",
                          border: "1px solid #cbd5e1",
                          boxSizing: "border-box",
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB 4: Life Pillars */}
          {activeTab === "pillars" && (
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: "12px",
                padding: "24px",
                boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: "18px", color: "#1e293b" }}>
                    University Life Pillars & Features
                  </h3>
                  <p style={{ margin: "4px 0 0", fontSize: "13px", color: "#64748b" }}>
                    The 2-column core sections (Campus Facilities, Societies, Sports, Skills, Women Development, Community).
                  </p>
                </div>

                <button
                  type="button"
                  onClick={handleAddPillar}
                  style={{
                    padding: "8px 16px",
                    backgroundColor: "#16a34a",
                    color: "#fff",
                    border: "none",
                    borderRadius: "6px",
                    fontWeight: 600,
                    fontSize: "13px",
                    cursor: "pointer",
                  }}
                >
                  + Add New Pillar
                </button>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                {pillars.map((item, idx) => (
                  <div
                    key={idx}
                    style={{
                      border: "1px solid #cbd5e1",
                      borderRadius: "10px",
                      padding: "16px",
                      backgroundColor: "#f8fafc",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                      <span style={{ fontWeight: 700, fontSize: "13px", color: "#2563eb" }}>
                        Pillar #{idx + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleDeletePillar(idx)}
                        style={{
                          border: "none",
                          background: "#fee2e2",
                          color: "#dc2626",
                          fontSize: "11px",
                          fontWeight: 600,
                          padding: "3px 8px",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                      >
                        Remove
                      </button>
                    </div>

                    <div style={{ marginBottom: "10px" }}>
                      <label style={{ display: "block", fontSize: "12px", color: "#475569", marginBottom: "4px", fontWeight: 600 }}>
                        Title
                      </label>
                      <input
                        type="text"
                        value={item.title || ""}
                        onChange={(e) => handlePillarChange(idx, "title", e.target.value)}
                        style={{
                          width: "100%",
                          padding: "8px 10px",
                          fontSize: "13px",
                          borderRadius: "6px",
                          border: "1px solid #cbd5e1",
                          boxSizing: "border-box",
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: "block", fontSize: "12px", color: "#475569", marginBottom: "4px", fontWeight: 600 }}>
                        Description
                      </label>
                      <textarea
                        rows={4}
                        value={item.description || ""}
                        onChange={(e) => handlePillarChange(idx, "description", e.target.value)}
                        style={{
                          width: "100%",
                          padding: "8px 10px",
                          fontSize: "13px",
                          borderRadius: "6px",
                          border: "1px solid #cbd5e1",
                          boxSizing: "border-box",
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Lightbox Modal */}
      {lightboxPhoto && (
        <div
          onClick={() => setLightboxPhoto(null)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.85)",
            zIndex: 99999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <div style={{ maxWidth: "90%", maxHeight: "90%", position: "relative" }}>
            <img
              src={lightboxPhoto}
              alt="Preview"
              style={{ maxWidth: "100%", maxHeight: "85vh", borderRadius: "8px", objectFit: "contain" }}
            />
            <button
              onClick={() => setLightboxPhoto(null)}
              style={{
                position: "absolute",
                top: "-12px",
                right: "-12px",
                backgroundColor: "#fff",
                border: "none",
                borderRadius: "50%",
                width: "32px",
                height: "32px",
                cursor: "pointer",
                fontWeight: "bold",
                fontSize: "16px",
              }}
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UniversityLifeAdmin;

